/* eslint-disable no-magic-numbers, react/no-multi-comp */
import React from 'react'
import { Icon, Paragraph } from '@scentregroup/ui'
import { formatTimeAMPM, formatDayAndMonth } from '../date-format-helpers'
import ExternalLink from '../../components/external-link'
import { ACCORDION_ORDER, DEFAULT_CALENDAR } from '../../constants'
import style from '../../components/info-accordion/index.module.scss'

const getFileName = (startAt, centreSlug) =>
  `calendar-invitation-${centreSlug}-${startAt.slice(0, 10)}.ics`

const getOccDate = (occ, centreTime) => {
  const { startAt: centreStart, endAt: centreEnd } = centreTime.fromEntryISO(
    occ
  )
  return `${centreTime.formatHumanDay(centreStart)}, ${formatTimeAMPM(
    centreStart
  )} - ${formatTimeAMPM(centreEnd)}`
}

const getPastOcc = (occ, centreTime) => {
  const { startAt: centreStart, endAt: centreEnd } = centreTime.fromEntryISO(
    occ
  )
  return `${centreTime.formatHumanDay(centreStart)} ${formatDayAndMonth(
    centreStart
  )}, ${formatTimeAMPM(centreStart)} - ${formatTimeAMPM(centreEnd)}`
}

const getOccTitle = centreStart => {
  const startDate = centreStart.format('D')
  const dateOrdinal = centreStart.format('Do').substr(-2)
  const month = centreStart.format('MMM')
  return (
    <div>
      <div type="large" className={style.rowTitleText}>
        {startDate}
        <span className={style.rowTitleSuperScript}>{dateOrdinal}</span>
      </div>
      <div className={style.rowSubtitleText}>{month}</div>
    </div>
  )
}

const getOccContent = (row, centreSlug, centreTime) => (
  <Paragraph>
    {getOccDate(row, centreTime)}
    {'. '}
    {row.calendar && (
      <ExternalLink
        className={style.rowLink}
        url={row.calendar[DEFAULT_CALENDAR]}
        download={getFileName(row.startAt, centreSlug)}
      >
        Add to Calendar
      </ExternalLink>
    )}
  </Paragraph>
)

const getOccurrenceRows = (occ, centreSlug, centreTime) => {
  const firstOcc = occ.times[0]
  const remainingOcc = occ.times.slice(1)
  let rows = [
    {
      rowType: 'occurrence',
      rows: [
        {
          title: getOccTitle(
            centreTime.fromISOIgnoringOffset(firstOcc.startAt)
          ),
          content: getOccContent(firstOcc, centreSlug, centreTime),
        },
      ],
    },
  ]
  if (occ.times.length > 1) {
    const rowsRemainingCount = remainingOcc.length
    rows = [
      ...rows,
      {
        expandTitle: {
          opened: 'Hide dates',
          closed: `Show ${rowsRemainingCount} more ${
            rowsRemainingCount === 1 ? 'date' : 'dates'
          }`,
        },
        rowType: 'occurrence',
        rows: remainingOcc.map(row => ({
          title: getOccTitle(centreTime.fromISOIgnoringOffset(row.startAt)),
          content: getOccContent(row, centreSlug, centreTime),
        })),
      },
    ]
  }
  return rows
}

const getRow = (rowType, rowText, centreSlug) => {
  if (rowText) {
    switch (rowType) {
      case 'location': {
        return {
          title: <Icon className={style.rowIcon} type="location-drop-pin" />,
          content: (
            <Paragraph>
              <span>{rowText}. </span>
              <ExternalLink
                className={style.rowLink}
                url={`/${centreSlug}/centre-map`}
              >
                View Map
              </ExternalLink>
            </Paragraph>
          ),
        }
      }
      case 'priceInfo': {
        return {
          title: <Icon className={style.rowIcon} type="offer" />,
          content: <Paragraph> Price: {rowText} </Paragraph>,
        }
      }
      default: {
        return {}
      }
    }
  } else {
    return null
  }
}

const getAccordionRows = (rowItems, centreSlug) => ({
  rowType: 'eventDetails',
  rows: rowItems
    .map(x => getRow(x.rowType, x.rowText, centreSlug))
    .filter(Boolean),
})
const isRowWithOccs = (rowType, entry) =>
  rowType.length === 1 && entry[rowType] && entry[rowType].times.length

const isRowWithText = (rowType, entry) =>
  Boolean(entry[rowType]) && { rowType, rowText: entry[rowType] }

export const getPastOccurrences = (pastOccurrences, centreTime) => [
  {
    expandTitle: {
      closed: 'Show Past Events',
      opened: 'Hide past events',
    },
    rowType: 'pastOccurrences',
    rows: pastOccurrences.map(pastOccurrence => ({
      content: getPastOcc(pastOccurrence, centreTime),
    })),
  },
]

export const getAccordionEventDetail = (entry, centreSlug, centreTime) =>
  ACCORDION_ORDER.reduce((acc, rowType) => {
    if (rowType[0] === 'occurrences') {
      if (isRowWithOccs(rowType, entry)) {
        return [
          ...acc,
          ...getOccurrenceRows(entry[rowType], centreSlug, centreTime),
        ]
      } else {
        return []
      }
    } else {
      const rowItems = rowType.map(x => isRowWithText(x, entry)).filter(Boolean)
      return [...acc, getAccordionRows(rowItems, centreSlug)]
    }
  }, [])
